import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import {
  AlertTriangle, Search, Eye, CheckCircle, XCircle,
  Clock, User, Calendar, TrendingUp, Download, UserCheck
} from 'lucide-react';
import { schoolAdminService, type AdminIncident } from '@/services/schoolAdminService';
import { safeLog } from '@/utils/safeLog';

function useSchoolAdminDarkMode() {
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('schoolAdminDarkMode') === 'true');
  useEffect(() => {
    const handleStorageChange = () => {
      setDarkMode(localStorage.getItem('schoolAdminDarkMode') === 'true');
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);
  return darkMode;
}

function severityBadge(severity: string, dm: boolean): string {
  const map: Record<string, [string, string]> = {
    low:      ['bg-blue-100 text-blue-800',     'bg-blue-900/40 text-blue-300'],
    medium:   ['bg-amber-100 text-amber-800',   'bg-amber-900/40 text-amber-300'],
    high:     ['bg-orange-100 text-orange-800', 'bg-orange-900/40 text-orange-300'],
    critical: ['bg-red-100 text-red-800',       'bg-red-900/40 text-red-300'],
  };
  return (map[severity] ?? ['bg-gray-100 text-gray-700', 'bg-gray-700 text-gray-300'])[dm ? 1 : 0];
}

function statusBadge(status: string, dm: boolean): string {
  const map: Record<string, [string, string]> = {
    new:          ['bg-purple-100 text-purple-800', 'bg-purple-900/40 text-purple-300'],
    acknowledged: ['bg-blue-100 text-blue-800',     'bg-blue-900/40 text-blue-300'],
    investigating:['bg-yellow-100 text-yellow-800', 'bg-yellow-900/40 text-yellow-300'],
    resolved:     ['bg-green-100 text-green-800',   'bg-green-900/40 text-green-300'],
    escalated:    ['bg-red-100 text-red-800',        'bg-red-900/40 text-red-300'],
    dismissed:    ['bg-gray-100 text-gray-800',      'bg-gray-700 text-gray-300'],
  };
  return (map[status] ?? ['bg-gray-100 text-gray-700', 'bg-gray-700 text-gray-300'])[dm ? 1 : 0];
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function timeAgo(dateString: string) {
  const secs = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);
  if (secs < 60) return 'Just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export function IncidentManagement({ initialSearch = '' }: { initialSearch?: string }) {
  const dm = useSchoolAdminDarkMode();
  const [incidents, setIncidents] = useState<AdminIncident[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [statusFilter, setStatusFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [selectedIncident, setSelectedIncident] = useState<AdminIncident | null>(null);
  const [quickLoading, setQuickLoading] = useState<string | null>(null);

  const cardCls = `border shadow-sm ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`;
  const titleCls = dm ? 'text-white' : 'text-slate-800';
  const descCls = dm ? 'text-white' : 'text-slate-500';
  const bodyTextCls = dm ? 'text-white' : 'text-slate-700';
  const rowHover = dm ? 'hover:bg-slate-700/40' : 'hover:bg-slate-50';
  const dividerCls = dm ? 'divide-slate-700' : 'divide-slate-100';
  const borderCls = dm ? 'border-slate-700' : 'border-slate-200';
  const selectContentCls = dm ? 'bg-slate-800 border-slate-700 text-white' : '';
  const selectItemCls = dm ? 'focus:bg-slate-700 focus:text-white text-white' : '';

  const fetchIncidents = useCallback(async () => {
    try {
      setLoading(true);
      const data = await schoolAdminService.getIncidents({
        status: statusFilter,
        severity: severityFilter,
        search: searchTerm,
      });
      setIncidents(data.incidents || []);
    } catch (error) {
      safeLog.error('Failed to fetch incidents:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, severityFilter, searchTerm]);

  useEffect(() => { fetchIncidents(); }, [statusFilter, severityFilter]);

  const handleStatusUpdate = async (incidentId: string, newStatus: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    try {
      setQuickLoading(incidentId + newStatus);
      await schoolAdminService.updateIncidentStatus(incidentId, newStatus);
      await fetchIncidents();
      if (selectedIncident?.id === incidentId) {
        setSelectedIncident(prev => prev ? { ...prev, status: newStatus as AdminIncident['status'] } : null);
      }
    } catch (error) {
      safeLog.error('Failed to update incident status:', error);
    } finally {
      setQuickLoading(null);
    }
  };

  const handleExport = () => {
    const escapeCSV = (f: string) =>
      /[",\n\r]/.test(f) || /^[=+\-@]/.test(f) ? `"${f.replace(/"/g, '""')}"` : f;
    const csv = [
      ['ID', 'Student', 'Type', 'Severity', 'Status', 'Created', 'Description'].join(','),
      ...incidents.map(inc => [
        escapeCSV(inc.id), escapeCSV(inc.studentName), escapeCSV(inc.alertType),
        inc.severity, inc.status,
        new Date(inc.createdAt).toLocaleDateString(),
        escapeCSV(inc.description),
      ].join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `incidents-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const stats = {
    total: incidents.length,
    new: incidents.filter(i => i.status === 'new').length,
    investigating: incidents.filter(i => i.status === 'investigating').length,
    resolved: incidents.filter(i => i.status === 'resolved').length,
  };

  return (
    <div className="space-y-6">

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        {[
          { label: 'Total Incidents', value: stats.total, icon: AlertTriangle, iconCls: descCls },
          { label: 'New', value: stats.new, icon: Clock, iconCls: 'text-purple-500' },
          { label: 'Investigating', value: stats.investigating, icon: TrendingUp, iconCls: 'text-yellow-500' },
          { label: 'Resolved', value: stats.resolved, icon: CheckCircle, iconCls: 'text-green-500' },
        ].map(({ label, value, icon: Icon, iconCls }) => (
          <Card key={label} className={cardCls}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className={`text-sm font-medium ${titleCls}`}>{label}</CardTitle>
              <Icon className={`h-4 w-4 ${iconCls}`} />
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${titleCls}`}>{loading ? '—' : value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters and list */}
      <Card className={cardCls}>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className={titleCls}>Incident Management</CardTitle>
              <CardDescription className={descCls}>Monitor and manage all incidents in your school</CardDescription>
            </div>
            <button onClick={handleExport} className="btn-glass btn-glass-pill flex items-center gap-2 text-sm">
              <Download className="h-4 w-4" />
              Export CSV
            </button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3 mb-6" data-dm={dm}>
            <div className="flex-1 min-w-48 relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by student name or type…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && fetchIncidents()}
                className={`pl-10 ${dm ? 'bg-slate-700 border-slate-600 text-white placeholder:text-white' : ''}`}
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className={`btn-glass btn-glass-pill w-40 ${dm ? 'text-slate-200' : ''}`}>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className={selectContentCls}>
                {['all', 'new', 'acknowledged', 'investigating', 'resolved', 'escalated'].map(v => (
                  <SelectItem key={v} value={v} className={selectItemCls}>
                    {v === 'all' ? 'All Status' : v.charAt(0).toUpperCase() + v.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={severityFilter} onValueChange={setSeverityFilter}>
              <SelectTrigger className={`btn-glass btn-glass-pill w-40 ${dm ? 'text-slate-200' : ''}`}>
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent className={selectContentCls}>
                {['all', 'low', 'medium', 'high', 'critical'].map(v => (
                  <SelectItem key={v} value={v} className={selectItemCls}>
                    {v === 'all' ? 'All Severity' : v.charAt(0).toUpperCase() + v.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Incidents list */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            </div>
          ) : incidents.length === 0 ? (
            <div className="text-center py-12">
              <AlertTriangle className={`h-12 w-12 mx-auto mb-4 ${descCls} opacity-40`} />
              <p className={descCls}>No incidents found</p>
            </div>
          ) : (
            <div className={`border rounded-xl overflow-hidden ${borderCls} ${dm ? 'bg-slate-800' : 'bg-white'}`}>
              <table className={`w-full text-sm ${dm ? 'bg-slate-800' : 'bg-white'}`}>
                <thead className={`border-b ${dm ? 'bg-slate-700/50 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                  <tr>
                    {['Student', 'Type', 'Severity', 'Status', 'Time', 'Actions'].map(h => (
                      <th key={h} className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide ${descCls}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className={`divide-y ${dividerCls} ${dm ? 'bg-slate-800' : 'bg-white'}`}>
                  {incidents.map(incident => {
                    const isNew = incident.status === 'new';
                    const isResolved = incident.status === 'resolved';
                    return (
                      <tr
                        key={incident.id}
                        className={`transition-colors cursor-pointer ${dm ? 'bg-slate-800' : 'bg-white'} ${rowHover}`}
                        onClick={() => setSelectedIncident(incident)}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${dm ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-600'}`}>
                              {incident.studentName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                            </div>
                            <span className={`text-sm font-medium ${bodyTextCls}`}>{incident.studentName}</span>
                          </div>
                        </td>
                        <td className={`px-4 py-3 text-sm capitalize ${descCls}`}>{incident.alertType?.replace(/_/g, ' ')}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${severityBadge(incident.severity, dm)}`}>
                            {incident.severity}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusBadge(incident.status, dm)}`}>
                            {incident.status}
                          </span>
                        </td>
                        <td className={`px-4 py-3 text-xs ${descCls}`}>
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {timeAgo(incident.createdAt)}
                          </div>
                        </td>
                        <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <button
                              className="btn-glass btn-glass-pill p-1.5"
                              title="View details"
                              onClick={() => setSelectedIncident(incident)}
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </button>
                            {isNew && (
                              <button
                                className="btn-glass btn-glass-pill btn-glass-purple px-2 py-1 text-xs flex items-center gap-1 disabled:opacity-50"
                                title="Acknowledge"
                                disabled={quickLoading === incident.id + 'acknowledged'}
                                onClick={e => handleStatusUpdate(incident.id, 'acknowledged', e)}
                              >
                                <UserCheck className="h-3 w-3" />
                                Ack
                              </button>
                            )}
                            {(isNew || incident.status === 'acknowledged') && (
                              <button
                                className="btn-glass btn-glass-pill btn-glass-blue px-2 py-1 text-xs flex items-center gap-1 disabled:opacity-50"
                                title="Start investigation"
                                disabled={quickLoading === incident.id + 'investigating'}
                                onClick={e => handleStatusUpdate(incident.id, 'investigating', e)}
                              >
                                <TrendingUp className="h-3 w-3" />
                                Investigate
                              </button>
                            )}
                            {!isResolved && (
                              <button
                                className="btn-glass btn-glass-pill btn-glass-emerald px-2 py-1 text-xs flex items-center gap-1 disabled:opacity-50"
                                title="Mark resolved"
                                disabled={quickLoading === incident.id + 'resolved'}
                                onClick={e => handleStatusUpdate(incident.id, 'resolved', e)}
                              >
                                <CheckCircle className="h-3 w-3" />
                                Resolve
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      {selectedIncident && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className={`w-full max-w-2xl max-h-[90vh] overflow-y-auto ${cardCls}`}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className={titleCls}>Incident Details</CardTitle>
                <button className="btn-glass p-1.5" onClick={() => setSelectedIncident(null)}>
                  <XCircle className="h-5 w-5" />
                </button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className={`text-sm font-medium ${descCls}`}>Student</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <User className={`h-4 w-4 ${descCls}`} />
                    <p className={`text-sm font-medium ${bodyTextCls}`}>{selectedIncident.studentName}</p>
                  </div>
                </div>
                <div>
                  <Label className={`text-sm font-medium ${descCls}`}>Alert Type</Label>
                  <p className={`text-sm font-medium mt-1 capitalize ${bodyTextCls}`}>{selectedIncident.alertType?.replace(/_/g, ' ')}</p>
                </div>
                <div>
                  <Label className={`text-sm font-medium ${descCls}`}>Severity</Label>
                  <div className="mt-1">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${severityBadge(selectedIncident.severity, dm)}`}>
                      {selectedIncident.severity}
                    </span>
                  </div>
                </div>
                <div>
                  <Label className={`text-sm font-medium ${descCls}`}>Status</Label>
                  <div className="mt-1">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusBadge(selectedIncident.status, dm)}`}>
                      {selectedIncident.status}
                    </span>
                  </div>
                </div>
              </div>

              {selectedIncident.description && (
                <div>
                  <Label className={`text-sm font-medium ${descCls}`}>Description</Label>
                  <p className={`text-sm mt-1 ${bodyTextCls}`}>{selectedIncident.description}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className={`text-sm font-medium ${descCls}`}>Created</Label>
                  <div className={`flex items-center gap-1 mt-1 text-sm ${bodyTextCls}`}>
                    <Calendar className={`h-3.5 w-3.5 ${descCls}`} />
                    {formatDate(selectedIncident.createdAt)}
                  </div>
                </div>
                <div>
                  <Label className={`text-sm font-medium ${descCls}`}>Last Updated</Label>
                  <p className={`text-sm mt-1 ${bodyTextCls}`}>{formatDate(selectedIncident.updatedAt)}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 pt-4 border-t" style={{ borderColor: dm ? '#334155' : '#e2e8f0' }}>
                {selectedIncident.status === 'new' && (
                  <button
                    onClick={() => handleStatusUpdate(selectedIncident.id, 'acknowledged')}
                    disabled={!!quickLoading}
                    className="btn-glass btn-glass-pill btn-glass-purple flex-1 disabled:opacity-50"
                  >
                    Acknowledge
                  </button>
                )}
                {(selectedIncident.status === 'new' || selectedIncident.status === 'acknowledged') && (
                  <button
                    onClick={() => handleStatusUpdate(selectedIncident.id, 'investigating')}
                    disabled={!!quickLoading}
                    className="btn-glass btn-glass-pill btn-glass-blue flex-1 disabled:opacity-50"
                  >
                    Start Investigation
                  </button>
                )}
                {selectedIncident.status !== 'resolved' && (
                  <button
                    onClick={() => handleStatusUpdate(selectedIncident.id, 'resolved')}
                    disabled={!!quickLoading}
                    className="btn-glass btn-glass-pill btn-glass-emerald flex-1 disabled:opacity-50"
                  >
                    Mark Resolved
                  </button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
